import { PageHeader } from '@/shared/components/ui/PageHeader';
// BizPlus parity Phase 4.3 — the generic report screen.
//
// ONE page for every report. Filters and columns are rendered from the server's
// registry definition, so adding a report needs no frontend change at all.
//
// Follows the BizPlus UX the client already knows: pick a report, set the dates,
// press Show, then export.

import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { FileBarChart, Play, Download, AlertTriangle } from 'lucide-react';
import { api, getApiErrorMessage } from '@/shared/api/axios';
import { Button } from '@/shared/components/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/components/ui/Card';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { EmptyState } from '@/shared/components/ui/EmptyState';
import { formatINR } from '@/shared/utils/currency';

interface ColumnDef { key: string; label: string; format: string }
interface FilterDef {
  key: string; kind: string; label: string; default?: string;
  // Server-declared choices for a 'status' filter (e.g. document type) — the
  // page renders whatever the registry sends, rather than hardcoding a list
  // per report the way PARENT_TYPES does for 'parent_type' below.
  options?: { value: string; label: string }[];
}
interface ReportDef {
  id: string; label: string; group: string; maturity: 'ready' | 'partial';
  limitations?: string | null; filters: FilterDef[]; columns: ColumnDef[];
}
interface RunResult {
  report: ReportDef;
  rows: Record<string, unknown>[];
  totals: Record<string, number> | null;
  count: number;
  maturity: string;
  limitations?: string | null;
}

const PARENT_TYPES = ['lead', 'enquiry', 'quotation', 'proforma', 'invoice', 'sales', 'service_ticket', 'deal'];

/** Today / first-of-month as YYYY-MM-DD from LOCAL parts, matching the server. */
function isoLocal(d: Date) {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}
function resolveDefault(def?: string) {
  const now = new Date();
  if (def === 'today') return isoLocal(now);
  if (def === 'start_of_month') return isoLocal(new Date(now.getFullYear(), now.getMonth(), 1));
  return '';
}

function renderCell(value: unknown, format: string) {
  if (value === null || value === undefined || value === '') return '—';
  if (format === 'money') return formatINR(Number(value) || 0);
  if (format === 'date') {
    const d = new Date(String(value));
    return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleDateString('en-IN');
  }
  if (format === 'integer') return Number(value).toLocaleString('en-IN');
  if (format === 'days') return `${value}d`;
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

export function ReportSuitePage() {
  const [selectedId, setSelectedId] = useState<string>('');
  const [filters, setFilters] = useState<Record<string, string>>({});
  // `runToken` is what actually triggers the fetch, so the grid only refreshes
  // when Show is pressed — not on every keystroke in a date field.
  const [runToken, setRunToken] = useState(0);

  const picker = useQuery({
    queryKey: ['report-picker'],
    queryFn: async () => (await api.get<{ groups: { group: string; reports: ReportDef[] }[] }>(
      '/api/reporting/reports',
    )).data,
  });

  const definition = useMemo(() => {
    for (const g of picker.data?.groups ?? []) {
      const found = g.reports.find((r) => r.id === selectedId);
      if (found) return found;
    }
    return null;
  }, [picker.data, selectedId]);

  // Reset the filters to the report's own declared defaults when it changes,
  // rather than carrying the previous report's values across.
  useEffect(() => {
    if (!definition) return;
    const next: Record<string, string> = {};
    for (const f of definition.filters) next[f.key] = resolveDefault(f.default);
    setFilters(next);
    setRunToken(0);
  }, [definition?.id]);

  const query = useMemo(() => {
    const p = new URLSearchParams();
    for (const [k, v] of Object.entries(filters)) if (v) p.set(k, v);
    return p.toString();
  }, [filters]);

  const result = useQuery({
    queryKey: ['report-run', selectedId, query, runToken],
    queryFn: async () => (await api.get<RunResult>(
      `/api/reporting/reports/${selectedId}?${query}`,
    )).data,
    enabled: Boolean(selectedId) && runToken > 0,
    retry: false,
  });

  useEffect(() => {
    if (result.error) toast.error(getApiErrorMessage(result.error, 'The report could not be run.'));
  }, [result.error]);

  const exportCsv = () => {
    if (!selectedId) return;
    // A normal navigation so the browser handles the download and the
    // Content-Disposition filename, rather than building a blob by hand.
    window.open(`/api/reporting/reports/${selectedId}/export?${query}`, '_blank');
  };

  const cols = result.data?.report?.columns ?? definition?.columns ?? [];

  return (
    <div className="space-y-4 pb-16">
      <PageHeader icon={FileBarChart} eyebrow="Insights" title="Reports" description={<>Pick a report, set the period, then Show. Only reports you have permission to run are listed.</>} />

      <Card>
        <CardBody className="flex flex-wrap items-end gap-3">
          <label className="min-w-56 text-xs">
            <span className="mb-1 block text-on-surface-variant">Report</span>
            <select
              className="h-9 w-full rounded border border-outline bg-surface px-2 text-data-md"
              value={selectedId}
              onChange={(e) => setSelectedId(e.target.value)}
            >
              <option value="">Select a report…</option>
              {(picker.data?.groups ?? []).map((g) => (
                <optgroup key={g.group} label={g.group}>
                  {g.reports.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
                </optgroup>
              ))}
            </select>
          </label>

          {/* Filters come from the definition — this page has no per-report markup. */}
          {(definition?.filters ?? []).map((f) => (
            <label key={f.key} className="text-xs">
              <span className="mb-1 block text-on-surface-variant">{f.label}</span>
              {f.kind === 'parent_type' ? (
                <select
                  className="h-9 rounded border border-outline bg-surface px-2 text-data-md"
                  value={filters[f.key] ?? ''}
                  onChange={(e) => setFilters((s) => ({ ...s, [f.key]: e.target.value }))}
                >
                  <option value="">All</option>
                  {PARENT_TYPES.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              ) : f.kind === 'status' && f.options ? (
                <select
                  className="h-9 rounded border border-outline bg-surface px-2 text-data-md"
                  value={filters[f.key] ?? ''}
                  onChange={(e) => setFilters((s) => ({ ...s, [f.key]: e.target.value }))}
                >
                  {f.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              ) : (
                <input
                  type="date"
                  className="h-9 rounded border border-outline bg-surface px-2 text-data-md"
                  value={filters[f.key] ?? ''}
                  onChange={(e) => setFilters((s) => ({ ...s, [f.key]: e.target.value }))}
                />
              )}
            </label>
          ))}

          <Button disabled={!selectedId || result.isFetching} onClick={() => setRunToken((n) => n + 1)}>
            <Play className="h-4 w-4 mr-1" /> {result.isFetching ? 'Running…' : 'Show'}
          </Button>
          <Button variant="secondary" disabled={!result.data?.rows?.length} onClick={exportCsv}>
            <Download className="h-4 w-4 mr-1" /> Export
          </Button>
        </CardBody>
      </Card>

      {/* A partial report is caveated rather than presented as complete. */}
      {result.data?.maturity === 'partial' && result.data.limitations && (
        <Card><CardBody className="flex items-start gap-2 text-sm">
          <AlertTriangle className="h-4 w-4 shrink-0 text-warning" />
          <span>{result.data.limitations}</span>
        </CardBody></Card>
      )}

      {runToken > 0 && (
        <Card>
          <CardHeader
            title={result.data?.report?.label || definition?.label || 'Report'}
            subtitle={result.data ? `${result.data.count.toLocaleString('en-IN')} row(s)` : undefined}
          />
          <CardBody className="p-0">
            {result.isFetching ? (
              <div className="p-6"><Skeleton className="h-48" /></div>
            ) : result.error ? (
              <p className="p-6 text-sm text-destructive">
                {getApiErrorMessage(result.error, 'The report could not be run.')}
              </p>
            ) : !result.data?.rows?.length ? (
              <div className="p-6">
                <EmptyState
                  title="No rows for this period"
                  description="The report ran successfully; there is simply nothing in the selected range."
                />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[700px] text-sm">
                  <thead className="bg-muted/50 text-left text-xs uppercase">
                    <tr>
                      {cols.map((c) => (
                        <th key={c.key}
                          className={`px-4 py-3 ${['money', 'integer', 'days'].includes(c.format) ? 'text-right' : ''}`}>
                          {c.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {result.data.rows.map((row, i) => (
                      <tr key={i} className="border-t">
                        {cols.map((c) => (
                          <td key={c.key}
                            className={`px-4 py-2 ${['money', 'integer', 'days'].includes(c.format) ? 'text-right tabular-nums' : ''}`}>
                            {renderCell(row[c.key], c.format)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                  {result.data.totals && (
                    <tfoot>
                      <tr className="border-t bg-muted/30 font-semibold">
                        {cols.map((c, i) => (
                          <td key={c.key}
                            className={`px-4 py-2 ${['money', 'integer', 'days'].includes(c.format) ? 'text-right tabular-nums' : ''}`}>
                            {i === 0 ? 'Total' : (result.data!.totals![c.key] !== undefined
                              ? renderCell(result.data!.totals![c.key], c.format) : '')}
                          </td>
                        ))}
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            )}
          </CardBody>
        </Card>
      )}
    </div>
  );
}
